import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  type Assertion,
  type CaseSourceType,
  type EvalCase,
  type EvalRun,
  type InputConfig,
  useCreateCaseMutation,
  useDeleteCaseMutation,
  useLazyGetRunQuery,
  useListFixtureSetsQuery,
  useRunCaseMutation,
  useUpdateCaseMutation,
} from '../api/evalApi';
import {
  assertionTypeOptions,
  defaultAssertionForType,
  getAssertionDescription,
  jsonPathOperatorOptions,
} from './EvalAssertionForm';
import { normalizeAssertions, validateCaseInputs } from './EvalCaseValidation';

const steps = ['Input', 'Assertions', 'Targets', 'Test & Save'] as const;

const stepTooltips: Record<string, string> = {
  Input: 'Define what to send to the AI and where it comes from',
  Assertions: 'Set the rules the AI\u2019s response must satisfy',
  Targets: 'Choose which AI providers to test against',
  'Test & Save': 'Run a test first \u2014 you can only save after it passes',
};

type Props = {
  open: boolean;
  suiteId: string;
  suitePromptId?: string;
  defaultProviders: string[];
  defaultProfileSlug?: string;
  preferredFixtureSetId?: string;
  initialCase?: EvalCase;
  onManageFixtures: (fixtureId?: string) => void;
  onClose: () => void;
  onSaved: () => void;
};

type EvalRunMode = 'pinned' | 'latest';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export default function EvalCaseEditorDialog({
  open,
  suiteId,
  suitePromptId,
  defaultProviders,
  defaultProfileSlug,
  preferredFixtureSetId,
  initialCase,
  onManageFixtures,
  onClose,
  onSaved,
}: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [promptId, setPromptId] = useState(suitePromptId ?? '');
  const [promptVersion, setPromptVersion] = useState(1);
  const [userMessage, setUserMessage] = useState('');
  const [providers, setProviders] = useState<string[]>(
    defaultProviders.length > 0 ? [defaultProviders[0]] : []
  );
  const [profileSlug, setProfileSlug] = useState(defaultProfileSlug ?? '');
  const [fixtureSetId, setFixtureSetId] = useState<string>('');
  const [inputType, setInputType] = useState<string>('text_generation');
  const [contextPersonId, setContextPersonId] = useState('');
  const [contextOrgId, setContextOrgId] = useState('');
  const [contextTranscript, setContextTranscript] = useState('');
  const [sourceType, setSourceType] = useState<CaseSourceType>('manual');
  const [sourceTraceId, setSourceTraceId] = useState('');
  const [sourceReviewed, setSourceReviewed] = useState(false);
  const [runMode, setRunMode] = useState<EvalRunMode>('pinned');
  const [assertions, setAssertions] = useState<Assertion[]>([{ type: 'contains', value: '' }]);
  const [status, setStatus] = useState<string | null>(null);
  const [draftCaseId, setDraftCaseId] = useState<string | null>(null);
  const [testRun, setTestRun] = useState<EvalRun | null>(null);

  const [createCase, { isLoading: isCreatingCase }] = useCreateCaseMutation();
  const [updateCase, { isLoading: isUpdatingCase }] = useUpdateCaseMutation();
  const [deleteCase] = useDeleteCaseMutation();
  const [runCase, { isLoading: isRunningCase }] = useRunCaseMutation();
  const [getRun] = useLazyGetRunQuery();
  const { data: fixtureData } = useListFixtureSetsQuery();

  const isBusy = isCreatingCase || isUpdatingCase || isRunningCase;
  const hasPassingRun =
    !!testRun && testRun.status === 'completed' && testRun.overall_pass === true;
  const isEditing = !!initialCase;

  const providerOptions = useMemo(() => {
    if (defaultProviders.length > 0) {
      return defaultProviders;
    }
    return ['openai:gpt-4o-mini', 'openai:gpt-4o', 'anthropic:claude-sonnet-5-5'];
  }, [defaultProviders]);

  const resetState = () => {
    setActiveStep(0);
    setName('');
    setDescription('');
    setPromptId(suitePromptId ?? '');
    setPromptVersion(1);
    setUserMessage('');
    setProviders(defaultProviders.length > 0 ? [defaultProviders[0]] : []);
    setProfileSlug(defaultProfileSlug ?? '');
    setFixtureSetId(preferredFixtureSetId ?? '');
    setInputType('text_generation');
    setContextPersonId('');
    setContextOrgId('');
    setContextTranscript('');
    setSourceType('manual');
    setSourceTraceId('');
    setSourceReviewed(false);
    setRunMode('pinned');
    setAssertions([{ type: 'contains', value: '' }]);
    setStatus(null);
    setDraftCaseId(null);
    setTestRun(null);
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveStep(0);
    setStatus(null);
    setTestRun(null);

    if (!initialCase) {
      setName('');
      setDescription('');
      setPromptId(suitePromptId ?? '');
      setPromptVersion(1);
      setUserMessage('');
      setProviders(defaultProviders.length > 0 ? [defaultProviders[0]] : []);
      setProfileSlug(defaultProfileSlug ?? '');
      setFixtureSetId(preferredFixtureSetId ?? '');
      setInputType('text_generation');
      setContextPersonId('');
      setContextOrgId('');
      setContextTranscript('');
      setSourceType('manual');
      setSourceTraceId('');
      setSourceReviewed(false);
      setRunMode('pinned');
      setAssertions([{ type: 'contains', value: '' }]);
      setDraftCaseId(null);
      return;
    }

    setName(initialCase.name);
    setDescription(initialCase.description ?? '');
    setPromptId(initialCase.prompt_ref?.prompt_id ?? suitePromptId ?? '');
    setPromptVersion(initialCase.prompt_ref?.version ?? 1);
    const message =
      typeof initialCase.input_config?.variables?.user_message === 'string'
        ? (initialCase.input_config.variables.user_message as string)
        : '';
    setUserMessage(message);
    setProviders(initialCase.target_config?.providers ?? defaultProviders);
    setProfileSlug(initialCase.target_config?.profile_slug ?? defaultProfileSlug ?? '');
    setFixtureSetId(initialCase.fixture_set_id ?? '');
    setInputType(initialCase.input_config?.type ?? 'text_generation');
    setContextPersonId(initialCase.input_config?.context?.person_id ?? '');
    setContextOrgId(initialCase.input_config?.context?.org_id ?? '');
    setContextTranscript(initialCase.input_config?.context?.transcript ?? '');
    setSourceType(initialCase.source_type ?? 'manual');
    setSourceTraceId(initialCase.source_trace_id ?? '');
    setSourceReviewed(initialCase.source_reviewed ?? false);
    setRunMode('pinned');
    setAssertions(
      initialCase.assertions.length > 0 ? initialCase.assertions : [{ type: 'contains', value: '' }]
    );
    setDraftCaseId(initialCase.id);
  }, [
    open,
    initialCase,
    suitePromptId,
    defaultProviders,
    defaultProfileSlug,
    preferredFixtureSetId,
  ]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const addAssertion = () =>
    setAssertions((current) => [...current, defaultAssertionForType('contains')]);

  const removeAssertion = (index: number) => {
    setAssertions((current) => current.filter((_, i) => i !== index));
  };

  const updateAssertion = (index: number, patch: Partial<Assertion>) => {
    setAssertions((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item))
    );
  };

  const setAssertionType = (index: number, type: Assertion['type']) => {
    setAssertions((current) =>
      current.map((item, i) => (i === index ? defaultAssertionForType(type) : item))
    );
  };

  const validateBeforeTest = () => {
    const validationError = validateCaseInputs({
      promptId,
      promptVersion,
      providers,
      assertions,
      sourceType,
      sourceTraceId,
      sourceReviewed,
    });
    if (validationError) {
      setStatus(validationError);
      return false;
    }
    return true;
  };

  const pollRunResult = async (runId: string): Promise<EvalRun> => {
    for (let attempts = 0; attempts < 20; attempts += 1) {
      const current = await getRun(runId, false).unwrap();
      if (current.status === 'completed' || current.status === 'failed') {
        return current;
      }
      await sleep(1000);
    }
    throw new Error('Timed out waiting for eval run completion.');
  };

  const handleTestRun = async () => {
    if (!validateBeforeTest()) {
      return;
    }

    try {
      setStatus('Creating draft case and running test...');

      let caseId = draftCaseId;
      const normalizedAssertions = normalizeAssertions(assertions);
      const body = {
        name: name.trim() || 'Draft Eval Case',
        description: description.trim(),
        prompt_ref: { prompt_id: promptId.trim(), version: promptVersion },
        input_config: {
          type: inputType as InputConfig['type'],
          variables: { user_message: userMessage },
          context:
            inputType === 'artifact_generation' || inputType === 'artifact_chat'
              ? {
                  person_id: contextPersonId || undefined,
                  org_id: contextOrgId || undefined,
                  transcript: contextTranscript || undefined,
                }
              : undefined,
        },
        assertions: normalizedAssertions,
        target_config: {
          providers,
          profile_slug: profileSlug || undefined,
        },
        fixture_set_id: fixtureSetId || undefined,
        source_type: sourceType,
        source_trace_id: sourceType === 'production_trace' ? sourceTraceId : undefined,
        source_reviewed: sourceType === 'production_trace' ? sourceReviewed : undefined,
        enabled: false,
      };

      // Always create a temporary disabled draft for the test run, even in edit
      // mode. This avoids mutating the existing case before the user explicitly
      // saves — if the test fails or the dialog is closed, the original is untouched.
      if (!draftCaseId) {
        const created = await createCase({
          suite_id: suiteId,
          ...body,
          enabled: false,
        }).unwrap();
        caseId = created.case.id;
        setDraftCaseId(caseId);
      } else {
        caseId = draftCaseId;
        await updateCase({ id: caseId, ...body, enabled: false }).unwrap();
      }

      const enqueue = await runCase({
        case_id: caseId,
        triggered_by: 'manual',
        run_mode: runMode,
      }).unwrap();
      const firstRunId = enqueue.run_ids[0];
      if (!firstRunId) {
        throw new Error('No run id returned from run enqueue response.');
      }

      const completed = await pollRunResult(firstRunId);
      setTestRun(completed);

      if (completed.status === 'completed' && completed.overall_pass) {
        setStatus('Test run passed. You can now save this case.');
      } else {
        setStatus('Test run did not pass. Adjust inputs/assertions and run again.');
        if (!isEditing) {
          await updateCase({ id: caseId, enabled: false }).unwrap();
        }
      }
    } catch (error: any) {
      setStatus(error?.data?.error ?? error?.message ?? 'Failed to run test.');
    }
  };

  const handleSave = async () => {
    if (!draftCaseId || !hasPassingRun) {
      return;
    }

    try {
      const normalizedAssertions = normalizeAssertions(assertions);
      const body = {
        name: name.trim() || 'New Eval Case',
        description: description.trim(),
        prompt_ref: { prompt_id: promptId.trim(), version: promptVersion },
        input_config: {
          type: inputType as InputConfig['type'],
          variables: { user_message: userMessage },
          context:
            inputType === 'artifact_generation' || inputType === 'artifact_chat'
              ? {
                  person_id: contextPersonId || undefined,
                  org_id: contextOrgId || undefined,
                  transcript: contextTranscript || undefined,
                }
              : undefined,
        },
        assertions: normalizedAssertions,
        target_config: {
          providers,
          profile_slug: profileSlug || undefined,
        },
        fixture_set_id: fixtureSetId || undefined,
        source_type: sourceType,
        source_trace_id: sourceType === 'production_trace' ? sourceTraceId : undefined,
        source_reviewed: sourceType === 'production_trace' ? sourceReviewed : undefined,
        enabled: true,
      };

      if (isEditing && initialCase) {
        // Edit mode: update the original case with the tested values,
        // then clean up the temporary draft.
        await updateCase({ id: initialCase.id, ...body }).unwrap();
        await deleteCase({ id: draftCaseId, suite_id: suiteId }).unwrap();
      } else {
        // New case mode: enable the draft case.
        await updateCase({ id: draftCaseId, ...body }).unwrap();
      }

      setStatus('Case saved.');
      onSaved();
      handleClose();
    } catch (error: any) {
      setStatus(error?.data?.error ?? error?.message ?? 'Failed to save case.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? 'Edit Eval Case' : 'New Eval Case'}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <Tooltip title={stepTooltips[label]} arrow placement="top">
                <StepLabel>{label}</StepLabel>
              </Tooltip>
            </Step>
          ))}
        </Stepper>

        {status && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatus(null)}>
            {status}
          </Alert>
        )}

        {activeStep === 0 && (
          <Stack spacing={2}>
            <TextField
              label="Case Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
            <TextField
              label="Prompt ID"
              value={promptId}
              onChange={(e) => setPromptId(e.target.value)}
              fullWidth
              helperText="The identifier of the prompt template being tested"
            />
            <TextField
              label="Prompt Version"
              type="number"
              value={promptVersion}
              onChange={(e) => setPromptVersion(Number(e.target.value))}
              fullWidth
              helperText="Which version of the prompt to evaluate"
            />
            <TextField
              label="User Message"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              multiline
              rows={4}
              fullWidth
              helperText="The message sent to the AI, usually a transcript plus an instruction"
            />

            <FormControl fullWidth>
              <InputLabel>Input Type</InputLabel>
              <Select
                label="Input Type"
                value={inputType}
                onChange={(e) => setInputType(e.target.value)}
              >
                <MenuItem value="text_generation">Text Generation</MenuItem>
                <MenuItem value="artifact_generation">Artifact Generation</MenuItem>
                <MenuItem value="artifact_chat">Artifact Chat</MenuItem>
                <MenuItem value="agent_behavior">Agent Behavior</MenuItem>
              </Select>
            </FormControl>

            {(inputType === 'artifact_generation' || inputType === 'artifact_chat') && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Scenario context — provide user/org scope for per-tenant prompt resolution:
                </Typography>
                <TextField
                  label="Person ID"
                  value={contextPersonId}
                  onChange={(e) => setContextPersonId(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="UUID of the person to simulate (for per-user prompt overrides)"
                />
                <TextField
                  label="Org ID"
                  value={contextOrgId}
                  onChange={(e) => setContextOrgId(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="UUID of the organization (for per-org prompt overrides)"
                />
                <TextField
                  label="Source Transcript"
                  value={contextTranscript}
                  onChange={(e) => setContextTranscript(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  helperText="The coaching transcript to summarize or generate feedback for"
                />
              </>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
              <FormControl fullWidth>
                <InputLabel>Fixture Set</InputLabel>
                <Select
                  label="Fixture Set"
                  value={fixtureSetId}
                  onChange={(e) => setFixtureSetId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(fixtureData?.fixture_sets ?? []).map((fixtureSet) => (
                    <MenuItem key={fixtureSet.id} value={fixtureSet.id}>
                      {fixtureSet.name} (v{fixtureSet.version})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Pre-built context data injected into the prompt</FormHelperText>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => onManageFixtures(fixtureSetId || undefined)}
                sx={{ minWidth: { sm: 180 } }}
              >
                Manage Fixture Sets
              </Button>
            </Stack>

            {fixtureSetId && (
              <Alert severity="info">
                {
                  (fixtureData?.fixture_sets ?? []).find(
                    (fixtureSet) => fixtureSet.id === fixtureSetId
                  )?.description
                }
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                label="Source"
                value={sourceType}
                onChange={(e) => {
                  const value = e.target.value as CaseSourceType;
                  setSourceType(value);
                  if (value === 'manual') {
                    setSourceTraceId('');
                    setSourceReviewed(false);
                  }
                }}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="production_trace">Production Trace</MenuItem>
              </Select>
              <FormHelperText>
                Where this test case comes from — manual or captured from a real conversation
              </FormHelperText>
            </FormControl>

            {sourceType === 'production_trace' && (
              <>
                <TextField
                  label="Trace ID"
                  value={sourceTraceId}
                  onChange={(e) => setSourceTraceId(e.target.value)}
                  fullWidth
                  helperText="ID of the production trace this case is derived from"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sourceReviewed}
                      onChange={(e) => setSourceReviewed(e.target.checked)}
                    />
                  }
                  label="I have reviewed this production trace and confirmed the inputs are safe to save"
                />
                {!sourceReviewed && (
                  <Alert severity="warning">
                    Production trace cases must be reviewed before saving. Check the box above to
                    confirm you have reviewed the trace data.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2}>
            {assertions.map((assertion, index) => (
              <Box
                key={`${assertion.type}-${index}`}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <FormControl fullWidth>
                    <InputLabel>Assertion Type</InputLabel>
                    <Select
                      label="Assertion Type"
                      value={assertion.type}
                      onChange={(e) => setAssertionType(index, e.target.value as Assertion['type'])}
                    >
                      {assertionTypeOptions.map((option) => (
                        <MenuItem key={option.type} value={option.type}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={() => removeAssertion(index)} aria-label="delete assertion">
                    <DeleteIcon />
                  </IconButton>
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {getAssertionDescription(assertion.type)}
                </Typography>

                {(assertion.type === 'contains' ||
                  assertion.type === 'not_contains' ||
                  assertion.type === 'regex') && (
                  <TextField
                    sx={{ mt: 1 }}
                    label={
                      assertion.type === 'contains'
                        ? 'Expected Text'
                        : assertion.type === 'not_contains'
                          ? 'Forbidden Text'
                          : 'Regex Pattern'
                    }
                    value={String(assertion.value ?? '')}
                    onChange={(e) => updateAssertion(index, { value: e.target.value })}
                    fullWidth
                  />
                )}

                {(assertion.type === 'min_length' || assertion.type === 'max_length') && (
                  <TextField
                    sx={{ mt: 1 }}
                    type="number"
                    label={assertion.type === 'min_length' ? 'Minimum Length' : 'Maximum Length'}
                    value={String(assertion.value ?? 0)}
                    onChange={(e) => updateAssertion(index, { value: Number(e.target.value) })}
                    fullWidth
                  />
                )}

                {assertion.type === 'json_path' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      label="Path"
                      value={assertion.path ?? ''}
                      onChange={(e) => updateAssertion(index, { path: e.target.value })}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        label="Operator"
                        value={assertion.operator ?? 'exists'}
                        onChange={(e) => updateAssertion(index, { operator: e.target.value })}
                      >
                        {jsonPathOperatorOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                )}

                {assertion.type === 'json_path' &&
                  (assertion.operator ?? 'exists') !== 'exists' && (
                    <TextField
                      sx={{ mt: 1 }}
                      label={
                        (assertion.operator ?? 'exists') === 'equals'
                          ? 'Expected Value'
                          : 'Expected Text'
                      }
                      value={String(assertion.value ?? '')}
                      onChange={(e) => updateAssertion(index, { value: e.target.value })}
                      fullWidth
                    />
                  )}

                {assertion.type === 'is_json' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    This assertion only checks valid JSON output and does not require additional
                    fields.
                  </Alert>
                )}

                {assertion.type === 'judge' && (
                  <>
                    <TextField
                      sx={{ mt: 1 }}
                      label="Rubric"
                      value={assertion.rubric ?? ''}
                      onChange={(e) => updateAssertion(index, { rubric: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                    />
                    <TextField
                      sx={{ mt: 1 }}
                      label="Threshold (0-1)"
                      type="number"
                      value={String(assertion.threshold ?? 0.7)}
                      onChange={(e) =>
                        updateAssertion(index, { threshold: Number(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 1, step: 0.1 }}
                      fullWidth
                    />
                  </>
                )}

                {assertion.type === 'tool_call_sequence' && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Tool names the agent must call in order (subsequence match):
                    </Typography>
                    {(Array.isArray(assertion.value) ? assertion.value : []).map(
                      (toolName: string, toolIdx: number) => (
                        <Stack
                          key={toolIdx}
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1 }}
                          alignItems="center"
                        >
                          <Chip label={toolIdx + 1} size="small" variant="outlined" />
                          <TextField
                            size="small"
                            value={toolName}
                            placeholder="tool_name"
                            onChange={(e) => {
                              const updated = [...(assertion.value as string[])];
                              updated[toolIdx] = e.target.value;
                              updateAssertion(index, { value: updated });
                            }}
                            fullWidth
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = (assertion.value as string[]).filter(
                                (_, i) => i !== toolIdx
                              );
                              updateAssertion(index, { value: updated });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )
                    )}
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const current = Array.isArray(assertion.value)
                          ? (assertion.value as string[])
                          : [];
                        updateAssertion(index, { value: [...current, ''] });
                      }}
                    >
                      Add Tool
                    </Button>
                  </Box>
                )}
              </Box>
            ))}

            <Button variant="outlined" startIcon={<AddIcon />} onClick={addAssertion}>
              Add Assertion
            </Button>
          </Stack>
        )}

        {activeStep === 2 && (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Providers</InputLabel>
              <Select
                label="Providers"
                multiple
                value={providers}
                onChange={(e) => setProviders(e.target.value as string[])}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {providerOptions.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Profile Slug (optional)"
              value={profileSlug}
              onChange={(e) => setProfileSlug(e.target.value)}
              fullWidth
              helperText="The system profile that shapes AI behavior (leave blank to use suite default)"
            />
          </Stack>
        )}

        {activeStep === 3 && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Run a test first. Save is only enabled after a passing run.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Run Mode</InputLabel>
              <Select
                label="Run Mode"
                value={runMode}
                onChange={(e) => setRunMode(e.target.value as EvalRunMode)}
              >
                <MenuItem value="pinned">Pinned (use selected prompt version)</MenuItem>
                <MenuItem value="latest">Latest (use newest published prompt)</MenuItem>
              </Select>
              <FormHelperText>
                Pinned keeps test results reproducible. Latest helps compare against the current
                prompt.
              </FormHelperText>
            </FormControl>
            <Button variant="contained" disabled={isBusy} onClick={handleTestRun}>
              {isBusy ? 'Running...' : 'Run Test'}
            </Button>

            {testRun && (
              <Alert severity={hasPassingRun ? 'success' : 'warning'}>
                Status: {testRun.status} | Provider: {testRun.provider}:{testRun.model}
                {testRun.error_message ? ` | Error: ${testRun.error_message}` : ''}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button disabled={activeStep === 0} onClick={() => setActiveStep((step) => step - 1)}>
          Back
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={() => setActiveStep((step) => step + 1)}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasPassingRun || !draftCaseId || isBusy}
          >
            Save Case
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
